import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Text } from "@/components/Text";
import type { NextStepCardSchema, OnboardingSchema } from "@/schemas/sections";
import { ChevronIcon } from "./icons";

/** The `list` layout: Elena's owned channels and the two upsells rendered as ONE
 *  flat chevron list under a single heading, instead of a channel block plus a
 *  grid of tone-coloured cards.
 *
 *  This deliberately de-emphasises the upsells. On the webinar variant the page
 *  has exactly one job — get the bot connected before the эфир — and a pair of
 *  saturated cards competes with it. Channels and upsells are the same kind of
 *  offer here ("somewhere to go next, optional"), so they read as one list.
 *
 *  `tone`/`emoji`/`badge` on a card and `emoji`/`cta` on a channel are inert in
 *  this layout by design; the `cards` layout still renders them. */
export function NextStepsList({
  heading,
  subheading,
  onboarding,
  cards,
}: {
  heading: string;
  subheading?: string;
  onboarding?: z.input<typeof OnboardingSchema>;
  cards: z.input<typeof NextStepCardSchema>[];
}) {
  const onboardingEnabled = !!onboarding && onboarding.enabled !== false;
  const channels = onboardingEnabled ? (onboarding?.channels ?? []) : [];
  const rows = [
    ...channels.map((channel) => ({
      key: `channel:${channel.href}`,
      title: channel.title,
      description: channel.description,
      href: channel.href,
      track: `lm_next.cta.onboarding_${channel.id}`,
      external: true,
    })),
    ...cards.map((card) => ({
      key: `card:${card.kind}:${card.href}`,
      title: card.title,
      description: card.description,
      href: card.href,
      track: `lm_next.cta.${card.kind}`,
      external: card.external ?? /^https?:\/\//.test(card.href),
    })),
  ];

  if (rows.length === 0) return null;

  return (
    <div data-component="NextStepsList" className="mx-auto mt-8 max-w-xl">
      <Text variant="title" as="h2" className="text-fg">
        {heading}
      </Text>
      {subheading && (
        <Text variant="body-sm" as="p" className="mt-1 text-muted">
          {subheading}
        </Text>
      )}
      <div className="mt-4 overflow-hidden rounded-3xl bg-bg px-4">
        {rows.map((row, index) => (
          <Flex
            as="a"
            key={row.key}
            href={row.href}
            {...(row.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            direction="row"
            align="center"
            gap={12}
            data-track={row.track}
            className={
              index === 0
                ? "group py-4 text-left no-underline"
                : "group border-border-subtle border-t py-4 text-left no-underline"
            }
          >
            <div className="min-w-0 flex-1">
              <p className="font-(weight:--fw-bold) text-fg">{row.title}</p>
              <p className="mt-0.5 text-(length:--type-body-sm) leading-(--lh-body) text-muted">
                {row.description}
              </p>
            </div>
            <span className="text-brand">
              <ChevronIcon />
            </span>
          </Flex>
        ))}
      </div>
      {onboardingEnabled && onboarding?.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-muted">
          {onboarding.note}
        </Text>
      )}
    </div>
  );
}

NextStepsList.displayName = "NextStepsList";
